import {
    IEditableEntity,
    IPropEventBroker,
    IReadablePropEventBroker,
} from '@zdrbm/zdr-native-tools';
import { AccessorFunction, Color } from '@deck.gl/core/typed';
import { GeometryTypes } from 'ui-components/google-map/components/geometry/geometry';
import {
    BasicRegionPropertiesV2,
    CbgTilesPropertiesV2,
    CityTilesProperties,
    CountyPropertiesV7,
    DowntownProperties,
    PumaProperties,
    TownProperties,
    VillageProperties,
    ZCTAProperties,
} from 'ui-components/google-map/components/regions-overlay/types';
import { LayerInputHandler } from 'deck.gl';
import type { PropSetOptions } from '@zdrbm/zdr-interfaces';
import { BasemapKeys } from '@advanced-mapping/pl-mapbox';
import type { Feature, Point, Polygon } from 'geojson';
import Tile2DHeader from '@deck.gl/geo-layers/tile-layer/tile-2d-header';

export enum LayerType {
    TILE = 'TILE',
    MVT = 'MVT',
}

export interface IDataSourceOptions {
    headers?: Record<string, string>;
    worker?: boolean;
}

export interface ITileLayerDataSource extends IDataSourceOptions {
    data: string[];
    maxZoom: number;
    minZoom: number;
}

export type PickingCallback<TAccessorInput> = LayerInputHandler<TAccessorInput>;
export type UpdateTriggersType<TTriggerTypes extends string> = Partial<
    Record<TTriggerTypes, string[]>
>;

export interface IBaseLayerState {
    layerId: string;
    visible: boolean;
    enabled: boolean;
}
export interface ITileLayerState extends IBaseLayerState {
    maxVisibleZoom: number;
    minVisibleZoom: number;
}

export interface IMVTLayerState extends ITileLayerState {}

export interface ILayerObject<TLayerObjectType> {
    getUniqueId(): string;
    getDisplayName(): string;
    getPopulation(): number;
    getLayerName(): string;
    getEntityId(): string;
    getCentroid(): [number, number] | undefined;
    /**
     * Polylabel returns the suggested coordinate of where a label should be placed.
     */
    getPolylabel(): [number, number] | undefined;
    getFocalPoint(): [number, number] | undefined;
    isEqual(other: Feature<any, TLayerObjectType>): boolean;
}

export type BaseLayerObjectOptions = {
    focalPoint?: [number, number];
};

export interface ILayer<
    TDAtaType = any,
    TTriggerTypes extends string = never,
    TLayerObjectType = any,
> extends IEditableEntity {
    getName(): string;
    getLayerType(): LayerType;
    getData(): TDAtaType;
    clearObjects(): void;
    setVisibility(visible: boolean, options?: PropSetOptions): boolean;

    /**
     * Enables/Disables the layer. When disabled, the layer's "visible" property will turn also to false.
     * As long as the layer is disabled, "visible" will not be able to be set to true.
     * When layer is enabled, it is not possible to modify the "visible" property.
     * @param enabled - true to enable the layer, false to disable it.
     *
     * @example
     * ```typescript
     * layer.setEnabled(true);
     * layer.visible.get(); // Either true or false
     * layer.setEnabled(false);
     * layer.visible.get(); // false
     * layer.setVisibility(true); // Will not work
     * ```
     */
    setEnabled(enabled: boolean): void;

    /**
     * Disables the "visible" option in a layer
     * @param disabled - true to disable the "visible" option, false to enable the "visible" option
     */
    setVisibilityDisabled(disabled: boolean): void;
    getState(): IBaseLayerState;
    loadState(state: IBaseLayerState): void;
    createLayerObject(info: TLayerObjectType, options?: BaseLayerObjectOptions): ILayerObject<any>;
    enabled: IReadablePropEventBroker<boolean>;
    visible: IReadablePropEventBroker<boolean>;

    // Disables the "visible" option
    visibilityDisabled: IReadablePropEventBroker<boolean>;

    opacity: IPropEventBroker<number>;
    loadOptions: IPropEventBroker<any>;

    highlightHandler: IPropEventBroker<PickingCallback<TLayerObjectType> | undefined>;
    selectHandler: IPropEventBroker<PickingCallback<TLayerObjectType>>;

    currentHighlightedObject: IPropEventBroker<ILayerObject<TLayerObjectType> | undefined>;
    currentSelectedObject: IPropEventBroker<ILayerObject<TLayerObjectType> | undefined>;
    updateTriggers: IPropEventBroker<UpdateTriggersType<TTriggerTypes>, string>;
}

export type RGBColor = [number, number, number];
export type RGBAColor = [number, number, number, number];

export interface IAccessors<TLayerObjectType> {
    getLineColor: IPropEventBroker<
        AccessorFunction<Feature<any, TLayerObjectType>, Color> | undefined
    >;
    getLineWidth: IPropEventBroker<
        AccessorFunction<Feature<any, TLayerObjectType>, number> | undefined
    >;
    getFillColor: IPropEventBroker<
        AccessorFunction<Feature<any, TLayerObjectType>, Color> | undefined
    >;
    getBorderLineColor: IPropEventBroker<
        AccessorFunction<Feature<any, TLayerObjectType>, Color> | undefined,
        string
    >;
    getBorderLineWidth: IPropEventBroker<
        AccessorFunction<Feature<any, TLayerObjectType>, number> | undefined,
        string
    >;
}

export interface ITileLayer<TLayerObjectType>
    extends IAccessors<TLayerObjectType>,
        ILayer<string[], keyof IAccessors<TLayerObjectType>, TLayerObjectType> {
    maxZoom: IReadablePropEventBroker<number>;
    minZoom: IReadablePropEventBroker<number>;
    color: IPropEventBroker<RGBColor, string>;
    width: IPropEventBroker<number, string>;
    borderLineColor: IPropEventBroker<RGBColor | undefined, string>;
    borderLineWidth: IPropEventBroker<number | undefined, string>;
    maxVisibleZoom: IPropEventBroker<number, string>;
    minVisibleZoom: IPropEventBroker<number, string>;
    onViewportLoad: IPropEventBroker<OnViewportLoad, string>;
    availableBoundaryLabelPoints: IPropEventBroker<BoundaryPointsLayer>;

    getColor(): RGBColor;
    restoreDefaults(minVisibleZoom: number, maxVisibleZoom: number): void;
    setZoomLevels(minZoom: number, maxZoom: number): void;
    getState(): ITileLayerState;
    loadState(state: ITileLayerState): void;
    isInZoomRange(currentMapZoom: number): boolean;
    setLineColorsByBasemap(basemapKey: BasemapKeys): void;
    setLineWidthByZoomLevel(zoom: number): void;
}

export interface IMVTLayer<TLayerObjectType> extends ITileLayer<TLayerObjectType> {
    binary: IPropEventBroker<boolean>;
    uniqueIdProperty: IPropEventBroker<string>;
    loadOptions: IPropEventBroker<{ fetch: RequestInit; worker: boolean }>;
    getState(): IMVTLayerState;
    loadState(state: IMVTLayerState): void;
}

export const regionTypesArray = [
    'DMA',
    'CBSA',
    'STATE',
    'COUNTY',
    'CITIES',
    'CBG',
    'DOWNTOWN',
    'PUMA',
    'ZCTA',
    'TOWN',
    'VILLAGE',
    'NEIGHBORHOOD',
    'RESIDENTIAL',
    'DISTRICT',
    'OFFICE',
    'RETAIL',
    'BID',
    'INDUSTRIAL',
    'MARKET',
    'UA',
    'COMMERCIAL_DISTRICT',
] as const;

// Derive the union type from the array
export type RegionTypes = typeof regionTypesArray[number];

export const CITY_TOWN_VILLAGE_GROUP = 'City / Town / Village';
export const SUBMARKET_GROUP = 'Submarket';
export const DISTRICT_NEIGHBORHOOD_GROUP = 'District / Neighborhood';
export const regionGroupTypes = [
    CITY_TOWN_VILLAGE_GROUP,
    SUBMARKET_GROUP,
    DISTRICT_NEIGHBORHOOD_GROUP,
] as const;
export type RegionGroupType = typeof regionGroupTypes[number];

export interface ILayerInitialData<TLayerObjectType> {
    id: string;
    name: string;
    visible?: boolean;
    enabled?: boolean;
    opacity?: number;
    highlightHandler?: PickingCallback<TLayerObjectType>;
    selectHandler?: PickingCallback<TLayerObjectType>;
    centerTilesWorker: CenterTilesWorker;
}

export interface ITileInitialData<TLayerObjectType> extends ILayerInitialData<TLayerObjectType> {
    loadOptions?: any;
    maxVisibleZoom: number;
    minVisibleZoom: number;
    color?: RGBColor;
    width?: number;
    borderLineColor?: RGBColor;
    borderLineWidth?: number;
}

export interface IMVTInitialData<TLayerObjectType> extends ITileInitialData<TLayerObjectType> {
    binary?: boolean;
    uniqueIdProperty?: string;
}

export type GeometryFeature<T> = Feature<GeometryTypes, T>;

export type RegionalPropertiesLayerType = GeometryFeature<BasicRegionPropertiesV2>;
export type CountyRegionalPropertiesLayerType = GeometryFeature<CountyPropertiesV7>;
export type CitiesRegionalPropertiesLayerType = GeometryFeature<CityTilesProperties>;
export type CbgRegionalPropertiesLayerType = GeometryFeature<CbgTilesPropertiesV2>;
export type DowntownRegionalPropertiesLayerType = GeometryFeature<DowntownProperties>;
export type PumaRegionalPropertiesLayerType = GeometryFeature<PumaProperties>;
export type ZctaRegionalPropertiesLayerType = GeometryFeature<ZCTAProperties>;
export type TownRegionalPropertiesLayerType = GeometryFeature<TownProperties>;
export type VillageRegionalPropertiesLayerType = GeometryFeature<VillageProperties>;

export interface ICountyLayerInitialData
    extends ITileInitialData<CountyRegionalPropertiesLayerType> {}
export interface ICitiesLayerInitialData
    extends ITileInitialData<CitiesRegionalPropertiesLayerType> {}
export interface IRegionalLayerInitialData extends IMVTInitialData<RegionalPropertiesLayerType> {}
export interface ICbgLayerInitialData extends ITileInitialData<CbgRegionalPropertiesLayerType> {}
export interface IDowntownLayerInitialData
    extends ITileInitialData<DowntownRegionalPropertiesLayerType> {}
export interface IPumaLayerInitialData extends ITileInitialData<PumaRegionalPropertiesLayerType> {}
export interface ITownLayerInitialData extends ITileInitialData<TownRegionalPropertiesLayerType> {}
export interface IVillageLayerInitialData
    extends ITileInitialData<VillageRegionalPropertiesLayerType> {}
export interface IZCTALayerInitialData extends ITileInitialData<ZctaRegionalPropertiesLayerType> {}

export interface IConfigurationStorageClient {
    store(data: Object): Promise<void>;
    load<T>(): Promise<T>;
    get<T>(): T;
}

// Map Tile Type
export type TilesData = (Tile2DHeader & { dataInWGS84: Feature<Polygon>[] })[];
export type BoundaryPointsLayer = Feature<Point>[];
export type OnViewportLoad = (tiles: TilesData) => Promise<void>;
export type CenterTilesWorker = (tiles: TilesData) => Promise<BoundaryPointsLayer>;
